import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  answerBlock
} from 'actions';

import LocalisedMessage from 'components/Common/LocalisedMessage';
import { HintButton } from 'components/Common/Buttons';

export const QuestionBlockRepeater = ({ block, onAnswer, preQualAnswers, messageValues }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [, setForceRender] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  const viewInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  const formatInstructions = (info) => {
    const { instructions } = info;

    if (typeof instructions === 'string') {
      return <LocalisedMessage id={ instructions } values={ messageValues } />;
    }

    if (Array.isArray(instructions) && instructions.every(instruction => typeof instruction === 'string')) {
      return (
        <div className="mt-3">
          {instructions.map(instruction => (
            <div className="mb-3">
              <LocalisedMessage id={ instruction } values={ messageValues } />
            </div>
          ))}
        </div>
      );
    }

    return instructions;
  };

  const blocks = useRef([]);

  const onBlockAnswer = (targetBlock, value, isValid) => {
    const blockIndex = blocks.current.findIndex(b => b.block.index === targetBlock.index);
    if (blockIndex > -1) {
      const newBlock = { ...blocks.current[blockIndex].block, ...{ answers: value, isValid } };
      blocks.current[blockIndex].block = newBlock;
      blocks.current[blockIndex].component = React.createElement(newBlock.component, {
        block: newBlock,
        onAnswer: onBlockAnswer,
        preQualAnswers,
        messageValues
      });
      setAnswer(blocks.current.map(b => b.block.answers || {}));
    }
  };

  const defaultBlocks = () => {
    if (block.minSize > 0) {
      return Array.from({ length: block.minSize ?? 0 }, (_, i) => {
        const newBlock = Object.assign({ index: i, answers: {} }, block.repeatBlock);
        return {
          block: newBlock,
          component: React.createElement(newBlock.component, {
            block: newBlock,
            onAnswer: onBlockAnswer,
            preQualAnswers,
            messageValues
          }),
          hidden: false
        };
      });
    }
    return [];
  };

  const onAddBlock = () => {
    const newBlocks = [...blocks.current];
    const nextIndex = blocks.current.length === 0 ? 0 : blocks.current[blocks.current.length - 1].block.index + 1;
    setAnswer([...answer, { index: nextIndex }]);
    const newBlock = Object.assign({ index: nextIndex }, block.repeatBlock);

    if (block.componentProps?.collapsible === true) {
      newBlocks.forEach((x, i) => { x.hidden = i >= (block.minSize ?? 0); });
    }

    newBlocks.push({
      block: newBlock,
      component: React.createElement(newBlock.component, {
        block: newBlock,
        onAnswer: onBlockAnswer,
        preQualAnswers,
        messageValues
      }),
      hidden: false
    });
    blocks.current = newBlocks;
  };

  const onRemoveBlock = (i) => {
    const newAnswer = [...answer];
    newAnswer.splice(i, 1);
    setAnswer(newAnswer);
    const newBlocks = [...blocks.current];
    newBlocks.splice(i, 1);

    if (block.componentProps?.collapsible === true) {
      newBlocks.forEach((x, xi) => { x.hidden = xi >= (block.minSize ?? 0); });
      if (i > 0) {
        newBlocks[i - 1].hidden = false;
      } else if (newBlocks.length > 0) {
        newBlocks[0].hidden = false;
      }
    }

    blocks.current = newBlocks;
  };

  const onExpandCollapseClick = (index, hidden) => {
    if (block.componentProps?.collapsible === true) {
      blocks.current.forEach((x, xi) => { x.hidden = xi >= (block.minSize ?? 0); });
      blocks.current[index].hidden = hidden;
      setForceRender(new Date().getTime());
    }
  };

  useEffect(() => {
    const initialBlocks = defaultBlocks();
    blocks.current = [...initialBlocks];
    setAnswer(new Array(block.minSize).fill({}));
  }, []);

  const isValid = () => {
    if (blocks.current.every(b => b.block.isValid === undefined)) {
      return undefined;
    }
    return !blocks.current.some(b => b.block.isValid === false);
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isValid()));
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      for (let i = 0; i < blocks.current.length; i += 1) {
        const newBlock = { ...blocks.current[i].block, ...{ isForceEdited: true } };
        blocks.current[i].block = newBlock;
        blocks.current[i].component = React.createElement(newBlock.component, {
          block: newBlock,
          onAnswer: onBlockAnswer,
          preQualAnswers,
          messageValues
        });
      }
      setForceRender(new Date().getTime());
    } else if (block.isForceEdited === undefined) {
      let reRender = false;
      for (let i = 0; i < blocks.current.length; i += 1) {
        if (blocks.current[i].block.isForceEdited === true) {
          reRender = true;
          const newBlock = { ...blocks.current[i].block, ...{ isForceEdited: undefined } };
          blocks.current[i].block = newBlock;
          blocks.current[i].component = React.createElement(newBlock.component, {
            block: newBlock,
            onAnswer: onBlockAnswer,
            preQualAnswers,
            messageValues
          });
        }
        if (reRender) {
          setForceRender(new Date().getTime());
        }
      }
    }
  }, [block.isForceEdited]);

  return (
    <div className="repeater">
      { blocks.current.map((bl, i) => <div className="repeater-item" key={ `${ bl.block.id }-${ bl.block.index }` }>
        <legend className="col-12 question-card-header">
          { block.componentProps?.collapsible && bl.block.index >= (block.minSize ?? 0) && <React.Fragment>
            <button className="show-hide-question-card" aria-expanded={ !bl.hidden } type="button" onClick={ () => onExpandCollapseClick(i, !bl.hidden) }>
              <FontAwesomeIcon icon={ `chevron-${ bl.hidden ? 'down' : 'up' }` } />
            </button>
            <h3 className="card-group">{ bl.block.title && <LocalisedMessage id={ bl.block.title } /> }</h3>
          </React.Fragment>}
          {bl.block.info?.instructions && <HintButton active={ showInstructions } hintType="hint" hintLabel={ intl.formatMessage({ id: bl.block.info?.instructions }) } onClick={ () => viewInstructions() } />}
          {(i + 1) > (block.minSize ?? 0) && <button
            className="btn-repeater btn-remove"
            type="button"
            onClick={ () => onRemoveBlock(i) }
            title={ intl.formatMessage({ id: 'questionnaire.repeater.remove' }) }
            aria-label={ intl.formatMessage({ id: 'questionnaire.repeater.remove' }) }
          >
            <FontAwesomeIcon icon="trash-alt" className="fa-xs" />
          </button>}
        </legend>
        <div className="col-12">
          {showInstructions && <div className="info mt-1">{formatInstructions(bl.block.info)}</div>}
        </div>
        <div className={ `container${ bl.hidden ? ' hide' : '' }` }>
          { bl.component }
        </div>
      </div>) }
      {
      (!block.maxSize || blocks.current.length < block.maxSize)
      && <button
        className="btn-repeater btn-add"
        type="button"
        onClick={ () => onAddBlock() }
        title={ intl.formatMessage({ id: 'questionnaire.repeater.add' }) }
        aria-label={ intl.formatMessage({ id: 'questionnaire.repeater.add' }) }
      >
        <FontAwesomeIcon icon="plus" className="fa-xs" />{ block.addText && <span><LocalisedMessage id={ block.addText } /></span> }
      </button>
      }
    </div>
  );
};

QuestionBlockRepeater.displayName = 'QuestionBlockRepeater';

QuestionBlockRepeater.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape(),
  messageValues: PropTypes.shape(),
};

QuestionBlockRepeater.defaultProps = {
  onAnswer: null,
  preQualAnswers: null,
  messageValues: {}
};

export default QuestionBlockRepeater;
